"use strict";
/**
 * The product module provides access to the product data from the EVA back-end
 * @preferred
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var base_1 = require("./base");
var service_1 = require("../service");
var _ = require("lodash");
var products;
(function (products) {
    function getQuantityOnHandForProducts(productIds, organisationId) {
        // Create the service instance
        //
        var service = new service_1.EVAService("GetQuantityOnHandForProducts");
        // Build the request data
        // The base class will add things like SessionID so keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            ProductIDs: productIds,
            OrganizationUnitID: organisationId,
        };
        // Perform the call and return resulting promise
        //
        return service.call(requestData);
    }
    products.getQuantityOnHandForProducts = getQuantityOnHandForProducts;
    function getProductPrices(productIds) {
        // Create the service instance
        //
        var service = new service_1.EVAService("GetProductPrices");
        // Build the request data
        // The base class will add things like SessionID so keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            ProductIDs: productIds
        };
        // Perform the call and return resulting promise
        //
        return service.call(requestData);
    }
    products.getProductPrices = getProductPrices;
    function getConfigurableProductsDetails(productIds, includes) {
        // Create the service instance
        //
        var service = new service_1.EVAService("GetConfigurableProductsDetail");
        // Build the request data
        // The base class will add things like SessionID so keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            ProductIDs: productIds,
            Includes: includes
        };
        // Perform the call and return resulting promise
        //
        return service.call(requestData);
    }
    products.getConfigurableProductsDetails = getConfigurableProductsDetails;
    function getProductsDetails(productIds, pageConfig, additionalFilters) {
        if (additionalFilters === void 0) { additionalFilters = {}; }
        var productList = new ProductList();
        if (pageConfig) {
            productList.setPageConfiguration(pageConfig);
        }
        else {
            productList.setPageConfiguration({
                Start: 0,
                Limit: 9999,
                SortDirection: 0,
                SortProperty: "product_id",
                Filter: {}
            });
        }
        productList.setFilter(_.extend(additionalFilters || {}, {
            "product_id": {
                "Values": productIds
            }
        }));
        return productList.fetch();
    }
    products.getProductsDetails = getProductsDetails;
    function getBundleProductsForProduct(productId, includedFields) {
        var service = new service_1.EVAService("GetBundleProductsForProduct");
        var requestData = {
            ProductID: productId,
            IncludedFields: includedFields
        };
        return service.call(requestData);
    }
    products.getBundleProductsForProduct = getBundleProductsForProduct;
    function getBundleProductDetails(bundleProductId, includedFields) {
        var service = new service_1.EVAService("GetBundleProductDetails");
        var requestData = {
            BundleProductID: bundleProductId,
            IncludedFields: includedFields
        };
        return service.call(requestData);
    }
    products.getBundleProductDetails = getBundleProductDetails;
})(products = exports.products || (exports.products = {}));
/**
 * Provides access to a product available in EVA
 */
var Product = /** @class */ (function (_super) {
    __extends(Product, _super);
    function Product() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Fetches the product details
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.Products.GetProductDetail, EVA.Core.Services.Products.GetProductDetailResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Product.prototype.fetch = function () {
        if (this.getId() === undefined) {
            return Promise.reject({
                status: 404,
                response: "Not found"
            });
        }
        else {
            // Build the request data
            //
            var requestData = {
                ID: this.getId()
            };
            return _super.prototype.fetch.call(this, "GetProductDetail", requestData);
        }
    };
    /**
     * Fetches the product configuration details
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetConfigurableProductDetail, EVA.Core.Services.GetConfigurableProductDetailResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Product.prototype.getConfigurationDetails = function () {
        if (this.getId() === undefined) {
            return Promise.reject({
                status: 404,
                response: "Not found"
            });
        }
        else {
            // Create our service instance
            //
            var service = new service_1.EVAService("GetConfigurableProductDetail");
            // Build the request data
            //
            var requestData = {
                ID: this.getId()
            };
            return service.call(requestData);
        }
    };
    /**
     * Retrieves the stock details for the product
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetStockDetailsForProduct, EVA.Core.Services.GetStockDetailsForProductResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Product.prototype.getStockDetails = function () {
        // Create our service instance
        //
        var service = new service_1.EVAService("GetStockDetailsForProduct");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            ProductID: this.getId()
        };
        // Perform the call and capture the resulting promise
        //
        return service.call(requestData);
    };
    /**
     * Retrieves the availability indication for the product
     * @param  {number}         quantityRequested           Quantity to check availability for. Defaults to 1
     * @param  {Array<number>}  organizationUnitIDs         An array of organisation id's to limit the availability check to
     * @param  {boolean}        fastestPickupShopOnly       Only return the fastest pickup option. Defaults to false
     * @param  {boolean}        includeAvailabilityTexts    Add availability text messages to the response. Defaults to false
     * @param  {boolean}        detailedShopInformation     Add detailed shop information to the response. Defaults to false
     * @return {Promise<Array<EVA.Core.ProductAvailabilityIndicationDto>>} A promise resolving when the fetch from the back-end has completed
     */
    Product.prototype.getAvailability = function (quantityRequested, organizationUnitIDs, fastestPickupShopOnly, includeAvailabilityTexts, detailedShopInformation) {
        var _this = this;
        if (quantityRequested === void 0) { quantityRequested = 1; }
        if (fastestPickupShopOnly === void 0) { fastestPickupShopOnly = false; }
        if (includeAvailabilityTexts === void 0) { includeAvailabilityTexts = false; }
        if (detailedShopInformation === void 0) { detailedShopInformation = false; }
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("GetAvailabilityIndication");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                Products: [{
                        ProductID: _this.getId(),
                        QuantityRequested: quantityRequested
                    }],
                FastestPickupShopOnly: fastestPickupShopOnly,
                OrganizationUnitIDs: organizationUnitIDs,
                DetailedShopInformation: detailedShopInformation,
                IncludeAvailabilityTexts: includeAvailabilityTexts
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function (result) {
                resolve(_.get(result, "response.Products") || []);
            })
                .catch(reject);
        });
    };
    Product.prototype.getExtendedAvailability = function (quantityRequested, shippingMethodId, options, organizationUnitId) {
        var _this = this;
        if (quantityRequested === void 0) { quantityRequested = 1; }
        if (shippingMethodId === void 0) { shippingMethodId = null; }
        return new Promise(function (resolve, reject) {
            var service = new service_1.EVAService("GetProductAvailability");
            var requestData = {
                Products: [{
                        ID: _this.getId(),
                        QuantityRequested: quantityRequested,
                        ShippingMethodID: shippingMethodId
                    }],
                Options: options,
                OrganizationUnitID: organizationUnitId
            };
            service.call(requestData)
                .then(function (result) {
                resolve(_.get(result, "response.Products") || []);
            })
                .catch(reject);
        });
    };
    /**
     * Retrieves the available variants (color, size, etc.) for a product
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.SearchProducts, EVA.Core.Services.SearchProductsResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    Product.prototype.getVariants = function (limit, start, sortDirection, sortProperty, additionalFilters) {
        if (limit === void 0) { limit = 15; }
        if (start === void 0) { start = 0; }
        if (sortDirection === void 0) { sortDirection = 0; }
        if (sortProperty === void 0) { sortProperty = "product_id"; }
        if (additionalFilters === void 0) { additionalFilters = {}; }
        // We will use the product search to find the child products for this product
        //
        var productVariants = new ProductList(limit, start, sortDirection, sortProperty);
        // Set this product as the parent
        //
        productVariants.setFilter(_.extend(additionalFilters || {}, {
            "parent_hierarchy": {
                "Values": [this.getId()]
            }
        }));
        productVariants.addIncludedField("product_id");
        productVariants.addIncludedField("clothing_primary_color");
        productVariants.addIncludedField("logical_level");
        productVariants.addIncludedField("clothing_size");
        productVariants.addIncludedField("display_price");
        productVariants.addIncludedField("product_media");
        productVariants.addIncludedField("is_sellable");
        return productVariants.fetch();
    };
    return Product;
}(base_1.FetchableItem));
exports.Product = Product;
/**
 * Provides access to a list of products available in EVA
 */
var ProductList = /** @class */ (function (_super) {
    __extends(ProductList, _super);
    function ProductList() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /**
         * The return aggregations flag
         * @type {any}
         */
        _this.includeAggregations = true;
        /**
         * The return suggestions flag
         * @type {any}
         */
        _this.includeSuggestions = false;
        /**
         * The query string to search for in the products catalog
         * @type {string}
         */
        _this.query = "";
        return _this;
    }
    /**
     * Returns the current list of included fields
     * @return {Array<string>} The configured list of included product fields
     */
    ProductList.prototype.getIncludedFields = function () {
        return this.includedFields;
    };
    /**
     * Returns the current list of included fields
     * @return {Array<string>} The configured list of included product fields
     */
    ProductList.prototype.addIncludedField = function (fieldName) {
        if (!_.isArray(this.includedFields)) {
            this.includedFields = [];
        }
        this.includedFields.push(fieldName);
        return this.includedFields;
    };
    /**
     * Clears the configured list of included fields
     * @return {Array<string>} The configured list of included product fields
     */
    ProductList.prototype.clearIncludedFields = function () {
        return this.includedFields = [];
    };
    /**
     * Sets the aggregation state
     * @return {any} The aggregation state
     */
    ProductList.prototype.setAggregationState = function (aggregationState) {
        return this.aggregationState = aggregationState;
    };
    /**
     * Return the currently set aggregation state
     * @return {string} The aggregation state
     */
    ProductList.prototype.getAggregationState = function () {
        return this.aggregationState;
    };
    /**
     * Sets the aggregation options
     * @return {any} The aggregation options
     */
    ProductList.prototype.setAggregationOptions = function (aggregationOptions) {
        return this.aggregationOptions = aggregationOptions;
    };
    /**
     * Return the currently set aggregation options
     * @return {any} The aggregation options
     */
    ProductList.prototype.getAggregationOptions = function () {
        return this.aggregationOptions;
    };
    /**
     * Returns the current query string
     * @return {string}       The configured query string
     */
    ProductList.prototype.getQuery = function () {
        return this.query;
    };
    /**
     * Set the query to search for
     * @param  {string} query The new query string
     * @return {string}       The configured query string
     */
    ProductList.prototype.setQuery = function (query) {
        return this.query = query;
    };
    /**
     * Return the aggregations that were returned with the product list
     * @return {any} The aggreations
     */
    ProductList.prototype.getAggregations = function () {
        return this.aggregations;
    };
    /**
     * Sets the current item details using the fetched service response.
     * Extending classes should override this method to pull the details from response
     * @param  {EVA.Core.Services.Products.SearchProductsResponse}  response    The response from the service call
     * @return {Array<EVA.Core.ProductDto>}                      The product items for the current page
     */
    ProductList.prototype.setData = function (response) {
        // The default response is usually EVA.Core.PagedResultGeneric
        // An extending class will know exactly what is returned and where
        // the data for <T> can be found
        //
        // Store the paging response information
        //
        this.currentPageNumber = response.Page;
        this.totalNumberOfPages = Math.ceil(response.Total / response.PageSize);
        this.totalNumberOfItems = response.Total;
        this.appliedFilter = response.Filters;
        // Store the aggregations
        //
        this.aggregations = response.Aggregations;
        this.aggregationState = response.AggregationState;
        this.aggregationOptions = response.AggregationOptions;
        // Set and return the page items
        //
        return this.data = response.Products;
    };
    /**
     * Fetches the product list item details
     * @return {Promise<IEVAServiceResponse<EVA.Core.ProductDto, EVA.Core.Services.SearchProducts, EVA.Core.Services.SearchProductsResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    ProductList.prototype.fetch = function () {
        // Ensure we have a sane page configuration
        //
        if (_.isEmpty(this.pageConfig)) {
            this.pageConfig = {
                Start: 0,
                Limit: 10,
                Filter: {},
                SortProperty: undefined,
                SortDirection: undefined
            };
        }
        // The SearchProduct calls works similar to a paged request but the page
        // configuration details are placed in seperate fields
        //
        var sortField = {
            FieldName: this.pageConfig.SortProperty,
            Direction: this.pageConfig.SortDirection
        };
        // Build the request data
        //
        var requestData = {
            Page: Math.floor(this.pageConfig.Start / this.pageConfig.Limit) + 1,
            PageSize: this.pageConfig.Limit,
            Filters: this.pageConfig.Filter,
            Sort: [sortField],
            IncludedFields: this.includedFields,
            AggregationState: this.aggregationState,
            AggregationOptions: this.aggregationOptions,
            Query: this.query || "",
        };
        return _super.prototype.fetch.call(this, "SearchProducts", requestData);
    };
    return ProductList;
}(base_1.FetchablePagedList));
exports.ProductList = ProductList;
